import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch,shallowEqual } from 'react-redux';
import {
    FilterHeader,
    FilterHeading,
    Reset,
    FilterContent,
    FilterPopup,
    SurgeryDateRangeWrapper
} from './index.styled';
import CommonFilter from 'src/common/refactoredComponents/filter/CommonFilter.jsx';
import BrandFilter from 'src/common/refactoredComponents/BrandFilter/index.jsx';
import Button from 'src/common/refactoredComponents/button/Button';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { useTranslation } from 'react-i18next';
import {debounce, arraysEqual,  checkNonDefault, isAnyDefaultValue, initFilterView, buildFilterState, toggleMultiSelectFilter, toggleSingleSelectFilter } from 'src/utils/util';
import BottomNavigation from 'src/common/refactoredComponents/bottomNavigation/BottomNavigation';
import { useViewportHeight } from 'src/utils/useViewportHeight';
import {setConsignedFilterData,getConsignedInventory,setDefaultTerritory,setConsignedSearchbarText} from 'src/pages/receiveSets/redux/actions';
import axios from 'src/api/Api';
import { fetchFilterData } from 'src/pages/receiveSets/redux/actions';

// Client-side search; "All" stays visible so it can always be selected
const filterBySearchText = (list, text) => {
    const term = text?.trim().toLowerCase();
    if (!term) return list;
    return list?.filter((item) => item.filterCode === 'All' || item.filterName?.toLowerCase().includes(term));
};

const FilterContainer = ({setIsOpen, isFilterApplied}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const searchbarText = useSelector(
        (state) => state.consignedInventoryReducer.searchbarText || {},
        shallowEqual
    );
    const debouncedFilterSearch = useRef(
        debounce((filterType, value) => {
            dispatch(fetchFilterData(filterType, value));
        }, 500)
    ).current;

    const { accountFilters, districtFilters, territoryFilters, consignedBrandFilters } = useSelector(
        (state) => state.consignedInventoryReducer.filterData || {},
        shallowEqual
    );
    const defaultTerritory = useSelector(
        (state) => state.consignedInventoryReducer.defaultTerritory,
        shallowEqual
    );
    const { districtUID, territoryUID, accountNumber, brand } = useSelector(
        (state) => state.consignedInventoryReducer.consignedAppliedFilters || {},
        shallowEqual
    );
    const { searchText, searchType } = useSelector(
        (state) => state.consignedInventoryReducer || {},
        shallowEqual
    );


    // // Helper functions to initialize filter views using generic utility
    const initDistrictView = (list) => initFilterView(list, 'allDistricts');
    const initTerritoryView = (list, appliedFilter) => {
        // Fall back to 'All' when a territory (default or applied) isn't present in the list
        const existsInList = (code) => list?.some(item => item.filterCode === code);
        const effectiveDefault = existsInList(defaultTerritory) ? defaultTerritory : 'All';
        const requestedCode = appliedFilter || effectiveDefault;
        const targetCode = existsInList(requestedCode) ? requestedCode : 'All';
        return list?.map(item => ({
            ...item,
            isChecked: list?.length === 1 ? true : item.filterCode === targetCode,
            isDefault: item.filterCode === (defaultTerritory || 'All')
        })) || [];
    };
    const initAccountView = (list) => initFilterView(list, 'All');
    const initBrandView = (list) => initFilterView(list, 'allBrands');

    // The popup remounts on every open, so selections are seeded from the applied filters
    const buildAppliedState = () => ({
        DistrictView: buildFilterState({ list: districtFilters, appliedFilter: districtUID ? [districtUID] : undefined, allCode: 'All' }),
        TerritoryView: initTerritoryView(territoryFilters, territoryUID),
        AccountView: buildFilterState({ list: accountFilters, appliedFilter: accountNumber ? [accountNumber] : undefined, allCode: 'All' }),
        BrandView: buildFilterState({ list: consignedBrandFilters, appliedFilter: brand, allCode: 'All' }),
    });
    const [filterState, setFilterState] = useState(buildAppliedState);
    const [preFilterState, setPreFilterState] = useState(buildAppliedState);

    const [expandedViews, setExpandedViews] = useState({
        DistrictView: false,
        TerritoryView: false,
        AccountView: false,
        BrandView:false
    });

    const bannerHeight = useSelector(
        (state) => state.loginReducer.bannerHeight
    );

    const visibleDistricts = useMemo(
        () => filterBySearchText(filterState.DistrictView, searchbarText.districtFilters),
        [filterState.DistrictView, searchbarText.districtFilters]
    );
    const visibleTerritories = useMemo(
        () => filterBySearchText(filterState.TerritoryView, searchbarText.territoryFilters),
        [filterState.TerritoryView, searchbarText.territoryFilters]
    );

    const updateExpandedViews = ({ view, value }) => {
        setExpandedViews((prev) => ({
            ...prev,
            [view]: value,
        }));
    };

    const getCheckedCodes = (items, codeKey = 'filterCode') => items?.filter(i => i.isChecked).map(i => i[codeKey]) || [];

    const hasFilterChanges = useMemo(() => {
        const views = ['DistrictView', 'TerritoryView', 'AccountView', 'BrandView'];
        for (const view of views) {
            if (!arraysEqual(getCheckedCodes(filterState[view]), getCheckedCodes(preFilterState[view]))) return true;
        }
        return false;
    }, [filterState, preFilterState]);
    
    const onCheckBoxChange = (event,filterName) => {
        const clickedValue = event.target.value;
        if (filterName === 'DistrictView') {
            setFilterState(prev => ({ ...prev, DistrictView: toggleSingleSelectFilter(prev.DistrictView, clickedValue) }));
            const obj = clickedValue !== 'All' ? {districtCode: clickedValue } : {};
            dispatch(fetchFilterData('territoryFilters', obj));
        } else if (filterName === 'TerritoryView') {
            // Account list is re-scoped to the new territory, so its selection goes back to 'All'
            setFilterState(prev => ({
                ...prev,
                TerritoryView: toggleSingleSelectFilter(prev.TerritoryView, clickedValue),
                AccountView: prev.AccountView?.map(item => ({ ...item, isChecked: item.filterCode === 'All' })),
            }));
            dispatch(setConsignedSearchbarText({ filterType: 'accountFilters', value: '' }));
             const obj = clickedValue !== 'All' ? {district: filterState?.DistrictView.find(item => item.isChecked)?.filterCode, territory: clickedValue } : {};
             dispatch(fetchFilterData('accountFilters', obj));
        } else if (filterName === 'AccountView') {
            setFilterState(prev => ({ ...prev, AccountView: toggleSingleSelectFilter(prev.AccountView, clickedValue) }));
        } else if (filterName === 'BrandView') {
            setFilterState(prev => ({ ...prev, BrandView: toggleMultiSelectFilter(prev.BrandView, clickedValue, 'All') }));
        }
    };

    const handleReset = useCallback(() => {
        setFilterState({
            DistrictView: initDistrictView(districtFilters),
            TerritoryView: initTerritoryView(territoryFilters),
            AccountView: initAccountView(accountFilters),
            BrandView: initBrandView(consignedBrandFilters),
        });
        dispatch(setConsignedFilterData(null));
        const params = {};
        if (searchText) {
            params.filterParam = searchType;
            params.searchQuery = searchText;
        }
        dispatch(getConsignedInventory(params));
        setIsOpen(false);
    }, [searchText, searchType]);

    const handleApply = () => {
         const selectedDistrictCodes = filterState?.DistrictView.find(item => item.isChecked)?.filterCode;
         const selectedTerritoryCodes = filterState?.TerritoryView.find(item => item.isChecked)?.filterCode;
         const selectedAccountsCodes = filterState?.AccountView.find(item => item.isChecked)?.filterCode;
         const selectedBrandCodes = filterState?.BrandView.filter(item => item.isChecked).map(item => item.filterCode);
         const selectedDistrictName = filterState?.DistrictView.find(item => item.isChecked && item.filterName !== 'All')?.filterName;
         const selectedTerritoryName = filterState?.TerritoryView.find(item => item.isChecked && item.filterName !== 'All')?.filterName;
         const selectedAccountName = filterState?.AccountView.find(item => item.isChecked && item.filterName !== 'All')?.filterName;
         const selectedBrandNames = filterState?.BrandView.filter(item => item.isChecked).map(item => item.filterName);
         const appliedFilterNames = [
            districtFilters?.length > 1  && selectedDistrictName !=='All' ? selectedDistrictName : null,
            territoryFilters?.length > 1 && selectedTerritoryName !=='All' ? selectedTerritoryName : null,
            accountFilters?.length > 0 && selectedAccountName !=='All' ? selectedAccountName : null,
            ...(consignedBrandFilters?.length > 1 && selectedBrandNames ? selectedBrandNames : [])
         ].filter(Boolean);
         const appliedFilters = {
            ...(districtFilters?.length > 1 && { districtUID: selectedDistrictCodes }),
            ...(territoryFilters?.length > 1 && { territoryUID: selectedTerritoryCodes }),
            ...((accountFilters?.length > 0 && selectedAccountsCodes !== 'All') && { accountNumber: selectedAccountsCodes }),
            ...(consignedBrandFilters?.length > 1 && { brand: selectedBrandCodes }),
            ...(searchText && { filterParam: searchType }),
            ...(searchText && { searchQuery: searchText }),
            appliedFilterNames
         }
         const { appliedFilterNames: _, ...filtersForApi } = appliedFilters;
         dispatch(setConsignedFilterData(appliedFilters));
         dispatch(getConsignedInventory(filtersForApi));
         setIsOpen(false);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    const handleSetDefault = async (view) => {
        if (view !== 'TerritoryView') return;
        const selectedTerritory = filterState?.TerritoryView?.find(item => item.isChecked);
        if (!selectedTerritory) return;

        const baseSiteId = localStorage.getItem('baseSiteId') || 'defaultSite';
        const url = `/occ/v2/${baseSiteId}/consignment/setDefaultFilter`;

        try {
            await axios.put(url, {
                territoryList: selectedTerritory.filterCode
            });
            // Update Redux state with new default territory
            dispatch(setDefaultTerritory(selectedTerritory.filterCode));
            // Update local state to reflect new default
            setFilterState(prev => ({
                ...prev,
                TerritoryView: prev.TerritoryView.map(item => ({
                    ...item,
                    isDefault: item.filterCode === selectedTerritory.filterCode
                }))
            }));
        } catch (error) {
            console.error('Failed to set default territory:', error);
        }
    };

    useEffectWithErrorLogging(() => {
        if (!districtFilters?.length && !territoryFilters?.length && !accountFilters?.length && !consignedBrandFilters?.length) return;

        const accountSearchActive = !!searchbarText.accountFilters?.trim();
        // Keep the current account selection; drop back to 'All' if it is not in the refreshed list
        const resolveAccountSelection = (currentSelection) => {
            const requested = currentSelection || accountNumber;
            if (!requested) return undefined;
            const isInList = accountFilters?.some(i => i.filterCode === requested);
            return isInList || accountSearchActive ? [requested] : ['All'];
        };

        // Preserve current selections when filter data changes; also applied to preFilterState to avoid false change detection
        const rebuildPreservingSelections = (prev) => {
            const currentDistrictSelection = prev.DistrictView?.find(i => i.isChecked)?.filterCode;
            const currentTerritorySelection = prev.TerritoryView?.find(i => i.isChecked)?.filterCode;
            const currentAccountSelection = prev.AccountView?.find(i => i.isChecked)?.filterCode;
            const currentBrandSelection = prev.BrandView?.filter(i => i.isChecked).map(i => i.filterCode);
            
            return {
                DistrictView: buildFilterState({ list: districtFilters, appliedFilter: [currentDistrictSelection || districtUID].filter(Boolean), allCode: 'All' }),
                TerritoryView: initTerritoryView(territoryFilters, currentTerritorySelection || territoryUID),
                // A lone account is auto-selected unless the list is narrowed by an account search
                AccountView: buildFilterState({ list: accountFilters, appliedFilter: resolveAccountSelection(currentAccountSelection), allCode: 'All', needtoCheckSingle: !accountSearchActive }),
                BrandView: buildFilterState({ list: consignedBrandFilters, appliedFilter: currentBrandSelection?.length ? currentBrandSelection : brand, allCode: 'All' }),
            };
        };

        setFilterState(rebuildPreservingSelections);
        setPreFilterState(rebuildPreservingSelections);
    }, [districtFilters, territoryFilters, accountFilters, consignedBrandFilters]);
    useEffectWithErrorLogging(() => {
        if (!districtFilters?.length && !territoryFilters?.length && !accountFilters?.length && !consignedBrandFilters?.length) return;
        // Reload territory/account lists for the applied selection, as an earlier unapplied change may have scoped them differently
        const selectedDistrict = filterState?.DistrictView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode;
        const selectedTerritory = filterState?.TerritoryView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode;
        dispatch(fetchFilterData('territoryFilters', selectedDistrict ? { districtCode: selectedDistrict } : {}));
        dispatch(fetchFilterData('accountFilters', { district: selectedDistrict, territory: selectedTerritory }));
    }, []);

    // District and territory search is local-only, so clear it when the popup closes
    useEffectWithErrorLogging(() => () => {
        dispatch(setConsignedSearchbarText({ filterType: 'districtFilters', value: '' }));
        dispatch(setConsignedSearchbarText({ filterType: 'territoryFilters', value: '' }));
        dispatch(setConsignedSearchbarText({ filterType: 'accountFilters', value: '' }));
    }, []);

    const isTerritoryFilterApplied = useMemo(() => {
        return checkNonDefault(filterState?.TerritoryView, defaultTerritory);
    }, [filterState?.TerritoryView]);
    const viewportHeight = useViewportHeight();

    // District and territory are filtered locally (see visibleDistricts / visibleTerritories); only account searches hit the API
    const handleSearchTextChange = (value, filterType) => {
        if (filterType === 'accountFilters') {
            const selectedDistrictCodes = filterState?.DistrictView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode;
            const selectedTerritoryCodes = filterState?.TerritoryView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode;
            debouncedFilterSearch(filterType, { district: selectedDistrictCodes, territory: selectedTerritoryCodes, searchParam: value });
        }
        dispatch(setConsignedSearchbarText({ filterType, value }));
    };


    return (
        <FilterPopup bannerHeight={bannerHeight} viewportHeight={viewportHeight}>
            <FilterHeader>
                <FilterHeading> {t('common.filter.heading')}</FilterHeading>
                <Reset disabled={!isFilterApplied} onClick={handleReset}>{t('common.filter.reset')}</Reset>
            </FilterHeader>
            <FilterContent>
            <CommonFilter
                key="district"
                header={t('filter.districtLabel')}
                filters={visibleDistricts}
                onCheckBoxChange={onCheckBoxChange}
                isExpanded={expandedViews.DistrictView}
                updateExpandedViews={updateExpandedViews}
                allFiltersWithStatus=""
                checkBoxName="DistrictView"
                view="DistrictView"
                isRadio={true}
                showSearchBar={districtFilters.length > 1}
                searchProduct={searchbarText.districtFilters || ''}
                updateSearch ={(v)=>handleSearchTextChange(v,'districtFilters')}
                 placeholderProp={t('admin.adminList.search') +
                                        ' ' +
                                        t('filter.districtLabel')
                                    }
            />
            <CommonFilter
                key="territory"
                header={t('filter.filterByTerritory')}
                filters={visibleTerritories}
                onCheckBoxChange={onCheckBoxChange}
                isExpanded={expandedViews.TerritoryView}
                updateExpandedViews={updateExpandedViews}
                allFiltersWithStatus=""
                checkBoxName="TerritoryView"
                view="TerritoryView"
                isRadio={true}
                isDefaultButton={filterState?.TerritoryView?.length > 1 && defaultTerritory}
                handleSetDefault={handleSetDefault}
                isFilterApplied={isTerritoryFilterApplied}
                showSearchBar={territoryFilters.length > 1}
                searchProduct={searchbarText.territoryFilters || ''}
                updateSearch ={(v)=>handleSearchTextChange(v,'territoryFilters')}
                 placeholderProp={t('admin.adminList.search') +
                                        ' ' +
                                        t('filter.filterByTerritory')
                                    }
            />
            <CommonFilter
                key="account"
                header={t('common.accountLabel')}
                filters={filterState.AccountView}
                onCheckBoxChange={onCheckBoxChange}
                isExpanded={expandedViews.AccountView}
                updateExpandedViews={updateExpandedViews}
                allFiltersWithStatus=""
                checkBoxName="AccountView"
                view="AccountView"
                isRadio={true}
                showSearchBar={true}
                searchProduct={searchbarText.accountFilters || ''}
                updateSearch ={(v)=>handleSearchTextChange(v,'accountFilters')}
                 placeholderProp={t('admin.adminList.search') +
                                        ' ' +
                                        t('common.accountLabel')
                                    }
            />
            <BrandFilter
                key="brandFilter"
                header={t('scheduleSurgery.productGuide.brandsText')}
                filters={filterState.BrandView}
                onCheckBoxChange={onCheckBoxChange}
                isExpanded={expandedViews.BrandView}
                updateExpandedViews={updateExpandedViews}
                checkBoxName="BrandView"
                view="BrandView"
                codeKey="filterCode"
                nameKey="filterName"
                persistSearchOnToggle
            />
            </FilterContent>
            <BottomNavigation
                justifyContent={'space-evenly'}
                opacity={'1'}
                position={'sticky'}
                padding={'11px'}
            >
                <Button
                    id={'common-fim-filter-cancel-button'}
                    show={true}
                    variant={ButtonStyles?.secondary}
                    width={'40%'}
                    height={'38px'}
                    clicked={handleCancel}
                >
                    {t('common.closeButton')}
                </Button>
                <Button
                    id={'common-fim-filter-apply-button'}
                    show={true}
                    variant={hasFilterChanges ? ButtonStyles?.primary : ButtonStyles?.disabled}
                    width={'40%'}
                    height={'38px'}
                    clicked={handleApply}
                    disabled={!hasFilterChanges}
                >
                    {t('common.applyButton')}
                </Button>
            </BottomNavigation>
        </FilterPopup>
    );
};

export default FilterContainer;
